import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAdminContractRenameApi } from "../hooks/useAdminContractRenameApi";
import type { ContractRenamePreviewResponse } from "../shared/adminContractRename";
import {
  canApplyContractRename,
  validateOptionalEventContractName,
} from "../shared/adminContractRename";

export type ContractRenameWorkflowProps = {
  initialGlobalEventId?: number | null;
  lockAnchorId?: boolean;
  embedded?: boolean;
  onApplied?: (res: { applied_count: number }) => void;
};

export function ContractRenameWorkflow({
  initialGlobalEventId = null,
  lockAnchorId = false,
  embedded = false,
  onApplied,
}: ContractRenameWorkflowProps) {
  const { previewContractRename, applyContractRename } = useAdminContractRenameApi();
  const [globalEventId, setGlobalEventId] = React.useState(
    initialGlobalEventId != null ? String(initialGlobalEventId) : ""
  );
  const [newRisingName, setNewRisingName] = React.useState("");
  const [newResolvingName, setNewResolvingName] = React.useState("");
  const [risingCurrent, setRisingCurrent] = React.useState<string | null>(null);
  const [resolvingCurrent, setResolvingCurrent] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<ContractRenamePreviewResponse | null>(null);
  const [confirmText, setConfirmText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [risingNameError, setRisingNameError] = React.useState<string | null>(null);
  const [resolvingNameError, setResolvingNameError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (initialGlobalEventId != null) {
      setGlobalEventId(String(initialGlobalEventId));
    }
  }, [initialGlobalEventId]);

  const loadCurrentNames = React.useCallback(async () => {
    const gid = Number(globalEventId);
    if (!Number.isFinite(gid) || gid <= 0) return;
    try {
      const res = await previewContractRename({
        global_event_id: gid,
        discover_only: true,
      });
      setRisingCurrent(res.rising_contract_name_current ?? null);
      setResolvingCurrent(res.resolving_contract_name_current ?? null);
    } catch {
      // Non-blocking; preview will still show current names in the table.
    }
  }, [globalEventId, previewContractRename]);

  React.useEffect(() => {
    if (initialGlobalEventId != null) {
      void loadCurrentNames();
    }
  }, [initialGlobalEventId, loadCurrentNames]);

  const buildPayload = (discoverOnly = false) => {
    const gid = Number(globalEventId);
    if (!Number.isFinite(gid) || gid <= 0) {
      throw new Error("Valid event ID is required.");
    }
    const risingTrim = newRisingName.trim();
    const resolvingTrim = newResolvingName.trim();
    if (!discoverOnly && !risingTrim && !resolvingTrim) {
      throw new Error("Enter a new rising and/or resolving contract name.");
    }
    const rErr = validateOptionalEventContractName(newRisingName);
    const sErr = validateOptionalEventContractName(newResolvingName);
    if (rErr || sErr) {
      throw new Error(rErr || sErr || "Invalid contract name");
    }
    return {
      global_event_id: gid,
      ...(risingTrim ? { new_rising_event_contract_name: risingTrim } : {}),
      ...(resolvingTrim ? { new_resolving_event_contract_name: resolvingTrim } : {}),
      ...(discoverOnly ? { discover_only: true } : {}),
    };
  };

  const runPreview = async () => {
    setError(null);
    setSuccess(null);
    setRisingNameError(validateOptionalEventContractName(newRisingName));
    setResolvingNameError(validateOptionalEventContractName(newResolvingName));
    setLoading(true);
    try {
      const payload = buildPayload(false);
      const res = await previewContractRename(payload);
      setPreview(res);
      setRisingCurrent(res.rising_contract_name_current ?? risingCurrent);
      setResolvingCurrent(res.resolving_contract_name_current ?? resolvingCurrent);
    } catch (e: unknown) {
      const err = e as Error & { responseBody?: string };
      setError(err.responseBody || err.message || "Preview failed.");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const payload = buildPayload(false);
      const res = await applyContractRename({
        ...payload,
        preview_token: preview.preview_token,
        confirm_text: confirmText,
      });
      setSuccess(`Renamed ${res.applied_count} row(s).`);
      setConfirmText("");
      onApplied?.({ applied_count: res.applied_count });
      await runPreview();
    } catch (e: unknown) {
      const err = e as Error & { responseBody?: string };
      setError(err.responseBody || err.message || "Apply failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rename all versions on the rising and/or resolving side. Leave a field empty to keep that side
          unchanged. Manufacturer events are excluded.
        </Typography>
      )}

      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Event ID"
          value={globalEventId}
          onChange={(e) => setGlobalEventId(e.target.value)}
          onBlur={() => void loadCurrentNames()}
          disabled={lockAnchorId}
          size="small"
          inputProps={{ inputMode: "numeric" }}
          helperText={lockAnchorId ? "Anchor event for this rename" : undefined}
        />
        <TextField
          label="New rising contract name"
          value={newRisingName}
          onChange={(e) => {
            const next = e.target.value.replace(/\s+/g, "");
            setNewRisingName(next);
            setRisingNameError(validateOptionalEventContractName(next));
          }}
          size="small"
          placeholder="Leave empty to keep rising unchanged"
          error={Boolean(risingNameError)}
          helperText={
            risingNameError ||
            (risingCurrent ? `Current: ${risingCurrent}` : "Optional — English PascalCase, no spaces")
          }
        />
        <TextField
          label="New resolving contract name"
          value={newResolvingName}
          onChange={(e) => {
            const next = e.target.value.replace(/\s+/g, "");
            setNewResolvingName(next);
            setResolvingNameError(validateOptionalEventContractName(next));
          }}
          size="small"
          placeholder="Leave empty to keep resolving unchanged"
          error={Boolean(resolvingNameError)}
          helperText={
            resolvingNameError ||
            (resolvingCurrent ? `Current: ${resolvingCurrent}` : "Optional — English PascalCase, no spaces")
          }
        />
        <Button variant="outlined" onClick={() => void runPreview()} disabled={loading}>
          Preview
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {preview && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Ready: {preview.ready_count}, conflicts: {preview.conflict_count}, unchanged:{" "}
            {preview.unchanged_count}, skipped: {preview.skipped_count ?? 0}. Rising and resolving may
            have different version counts.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Side</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Ver.</TableCell>
                <TableCell>Old name</TableCell>
                <TableCell>New name</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.rows.map((row) => (
                <TableRow key={`${row.global_event_id}-${row.side}`}>
                  <TableCell>{row.side}</TableCell>
                  <TableCell>{row.global_event_id}</TableCell>
                  <TableCell>{row.event_code}</TableCell>
                  <TableCell>{row.version ?? "—"}</TableCell>
                  <TableCell>{row.event_contract_name_old}</TableCell>
                  <TableCell>{row.event_contract_name_new}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === "ready"
                          ? "success"
                          : row.status === "conflict"
                            ? "error"
                            : "default"
                      }
                    />
                    {row.reason ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.reason}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <TextField
              label="Type APPLY to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              size="small"
            />
            <Button
              variant="contained"
              color="warning"
              disabled={
                !canApplyContractRename(
                  loading,
                  preview.conflict_count,
                  preview.ready_count,
                  confirmText
                )
              }
              onClick={() => void apply()}
            >
              Apply rename
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
